/**
 * Created by Simone.Sacchi on 01/08/2016.
 */

"use strict";

require("Base/importDesignSystem");

const ClientStorage = require("Base/ClientStorage");
const baseConfig = require("Base/config");
const utils = require("Base/utils");
const ConcurrentUsers = require("Base/ConcurrentUsers");


window._ = {};

/**
 * Vanilla JS replacements for lodash functions.
 * Replaces the `lodash` npm dependency.
 */

/**
 * Assign source properties to destination (like Object.assign).
 * Replaces lodash/extend.
 */
window._.extend = function (destination) {
    if (destination == null) {
        return destination;
    }
    const sources = Array.prototype.slice.call(arguments, 1);
    sources.forEach((source) => {
        if (source != null) {
            const keys = Object.keys(source);
            for (const key of keys) {
                destination[key] = source[key];
            }
        }
    });
    return destination;
};

/**
 * Iterate over a collection (array or object).
 * Replaces lodash/each.
 */
window._.each = function (collection, iteratee) {
    if (collection == null) {
        return collection;
    }
    if (Array.isArray(collection)) {
        for (let i = 0; i < collection.length; i++) {
            if (iteratee(collection[i], i, collection) === false) {
                break;
            }
        }
    } else {
        const keys = Object.keys(collection);
        for (const key of keys) {
            if (iteratee(collection[key], key, collection) === false) {
                break;
            }
        }
    }
    return collection;
};

/**
 * Check if value is empty (null, undefined, empty array, empty string, empty object).
 * Replaces lodash/isEmpty.
 */
window._.isEmpty = function (value) {
    if (value == null) {
        return true;
    }
    if (Array.isArray(value) || typeof value === 'string') {
        return value.length === 0;
    }
    if (typeof value === 'object') {
        return Object.keys(value).length === 0;
    }
    return true;
};

/**
 * Returns an array of unique values.
 * Replaces lodash/uniq.
 */
window._.uniq = function (arr) {
    if (!Array.isArray(arr)) {
        return [];
    }
    return arr.filter((item, index, self) => {
        return self.indexOf(item) === index;
    });
};

/**
 * Check if value is an object (not null, typeof object or function).
 * Already vanilla, kept for clarity.
 */
window._.isObject = function isObject(value) {
    const type = typeof value;
    return value != null && (type === 'object' || type === 'function');
};

/**
 * Deep equality comparison between two values.
 * Replaces lodash/isEqual.
 */
window._.isEqual = function (a, b) {
    // Same reference or both primitives equal
    if (a === b) {
        return true;
    }

    // If either is null/undefined (and not both, since a===b already handled)
    if (a == null || b == null) {
        return a === b;
    }

    // Handle Date objects
    if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime();
    }

    // Handle RegExp objects
    if (a instanceof RegExp && b instanceof RegExp) {
        return a.source === b.source && a.flags === b.flags;
    }

    // If types differ, not equal
    if (typeof a !== typeof b) {
        return false;
    }

    // Handle arrays
    if (Array.isArray(a) && Array.isArray(b)) {
        return isArraysEqual(a, b);
    }

    // Handle plain objects
    if (typeof a === 'object' && typeof b === 'object') {
        return isObjectsEqual(a, b);
    }

    // For all other cases (numbers, strings, booleans, etc.)
    return false;
};

const isArraysEqual = function (a, b) {
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        if (!window._.isEqual(a[i], b[i])) {
            return false;
        }
    }
    return true;
};

const isObjectsEqual = function (a, b) {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) {
        return false;
    }

    for (const key of keysA) {
        if (!b.hasOwnProperty(key)) {
            return false;
        }
        if (!window._.isEqual(a[key], b[key])) {
            return false;
        }
    }

    return true;
};

/**
 * Filter array by predicate.
 * Replaces lodash/filter.
 */
window._.filter = function (collection, predicate) {
    if (collection == null) {
        return [];
    }
    if (Array.isArray(collection)) {
        return typeof predicate === 'function' ? collection.filter(function (item) { return predicate(item); }) : [];
    }
    // Object support: iterate over values, passing (value, key, collection)
    if (typeof collection === 'object') {
        const result = [];
        const keys = Object.keys(collection);
        for (const key of keys) {
            const value = collection[key];
            if (predicate(value, key, collection)) {
                result.push(value);
            }
        }
        return result;
    }
    return [];
};

/**
 * HTML-escape a string.
 * Replaces lodash/escape.
 */
window._.escape = function (str) {
    if (str == null) {
        return '';
    }
    return (`${  str}`).replace(/[&<>"']/g, (match) => {
        switch (match) {
            case '&': return '&amp;';
            case '<': return '&lt;';
            case '>': return '&gt;';
            case '"': return '&quot;';
            case "'": return '&#x27;';
            default: return match;
        }
    });
};

/**
 * Array difference (values in array not in others).
 * Replaces lodash/difference.
 */
window._.difference = function (arr) {
    if (!Array.isArray(arr)) {
        return [];
    }
    const others = Array.prototype.slice.call(arguments, 1);
    const excludeSet = {};
    others.forEach((other) => {
        if (Array.isArray(other)) {
            other.forEach((item) => {
                excludeSet[item] = true;
            });
        }
    });
    return arr.filter((item) => {
        return !excludeSet.hasOwnProperty(item);
    });
};

/**
 * Sort array by property or iteratee.
 * Replaces lodash/sortBy.
 */
window._.sortBy = function (arr, iteratee) {
    if (!Array.isArray(arr)) {
        return [];
    }
    const cloned = arr.slice();
    return cloned.sort((a, b) => {
        const aVal = typeof iteratee === 'function' ? iteratee(a) : a[iteratee];
        const bVal = typeof iteratee === 'function' ? iteratee(b) : b[iteratee];
        if (aVal < bVal) return -1;
        if (aVal > bVal) return 1;
        return 0;
    });
};

/**
 * Find first element matching predicate.
 * Replaces lodash/find.
 */
window._.find = function (collection, predicate) {
    if (!Array.isArray(collection)) {
        return undefined;
    }
    if (typeof predicate !== 'function') {
        return undefined;
    }
    return collection.filter(function (item) { return predicate(item); })[0];
};

/**
 * Extract values from an object.
 * Replaces lodash/values.
 */
window._.values = function (obj) {
    if (obj == null || typeof obj !== 'object') {
        return [];
    }
    return Object.keys(obj).map((key) => {
        return obj[key];
    });
};

/**
 * Create an object keyed by a property of each array element.
 * Replaces lodash/indexBy (lodash v3 name for _.keyBy).
 */
window._.indexBy = function (arr, key) {
    if (!Array.isArray(arr)) {
        return {};
    }
    return arr.reduce((acc, item) => {
        acc[item[key]] = item;
        return acc;
    }, {});
};

/**
 * Create a debounced function.
 * Replaces lodash/debounce.
 */
window._.debounce = function (func, wait) {
    let timeout;
    return function () {
        const context = this;
        const args = arguments;
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            func.apply(context, args);
        }, wait);
    };
};


$.ajaxSetup({
    headers: {
        'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content')
    },
    /**
     * Strip XSSI protecting prefix from JSON using jQuery ajaxBefore - prefix is "for (;;);"
     *
     * @link https://gist.github.com/simonw/46695d184096e917c3897d6b8ec20de3
     *
     * @param {string} response
     * @returns {string} response with out prefix
     *
     * @author Simone Sacchi
     * @version 2018/10/17
     */
    dataFilter (response) {
        response = response.replace(/^for \(;;\);/, '');

        return response;
    }
});

//define the base url
const _baseUrl = window.Swiftref.base_url;



/*
 *
 *  Concurrent users check
 *
 *
 */
// INSTITUTION DETAILS - tab and link
$("#search__details__content--institutions").on("mousedown", (e) => {
    // set default page
    let requestedPage = 'institutions_institution_profile';

    const element = e.target.tagName;

    if (element == 'A' ||
        element == 'SELECT' ||
        element == 'INPUT' ||
        (element === 'TR' && e.target.dataset.acronym)
    ) {

        if (e.target.getAttribute('aria-controls')) {
            requestedPage = ConcurrentUsers.mappingRequestedPage.institution_details[e.target.getAttribute('aria-controls')];
        }

        ConcurrentUsers.check(e, requestedPage, () => {
            // set default element to hidden
            $('#search__details__tab_content--institutions_institution_profile').html('');

            $(`#search__details__tab_content--${  requestedPage}`).html('');

        }, `#search__details__tab_content--${  requestedPage}`);
    }
});


// SSIS DETAILS - tab and link
$("#search__details__content--ssis").on("mousedown", (e) => {
    const element = e.target.tagName;

    if (element == 'A' ||
        element == 'SELECT' ||
        element == 'INPUT'
    ) {
        const requestedPage = ConcurrentUsers.mappingRequestedPage.ssis_details;
        ConcurrentUsers.check(e, requestedPage, () => {

            $("#search__details__tab_content--ssis_content").html('');

            if (element == 'A' && $(e.target).hasClass('default_link')) {
                setTimeout(() => {
                    window.location.href = `${_baseUrl  }/${  window.Swiftref.lang  }/home`;
                }, 5000);
            }
        }, '#search__details__tab_content--ssis_content');
    }

});

// PAYMENT SYSTEM DETAILS
$("#search__details__content--payment_systems").on("mousedown", (e) => {

    const element = e.target.tagName;

    if (element == 'A' ||
        element == 'SELECT' ||
        element == 'INPUT'
    ) {
        const requestedPage = ConcurrentUsers.mappingRequestedPage.payment_systems_details;
        ConcurrentUsers.check(e, requestedPage, () => {

            $("#payment_system_details").html('');
            $("#payment_systems_details_table__records").html('');
            $("#payment_systems_details_table__pagination").html('');

        }, '#payment_system_details');
    }

});


// Countries currencies details - tab
$("#search__details__tabs--countries_currencies").on("mousedown", (e) => {
    const element = e.target.tagName;

    if (element == 'A' ||
        element == 'SELECT' ||
        element == 'INPUT' ||
        (element === 'TR' && e.target.dataset.acronym)

    ) {
        const requestedPage = ConcurrentUsers.mappingRequestedPage['countries_currencies'];
        const divContainer = e.target.getAttribute('aria-controls');

        ConcurrentUsers.check(e, requestedPage, () => {
            // hide the first item
            $('#details_countries_currencies__main_information').html('');

            $(`#details_countries_currencies__${  divContainer}`).html('');

        }, `#details_countries_currencies__${  divContainer}`);
    }


});


try {
    document.querySelector("#copy-year").innerText = new Date().getFullYear();
    const newFeedbackButton = document.getElementById("new-feedback-button");
    if (newFeedbackButton) {
        newFeedbackButton.addEventListener("click", (e) => {
            e.preventDefault();
            try {
                // public pages (e.g.: sepaio) does not have feedback button
                document.getElementById("old-feedback-button").click();
            } catch (e) {}
        });
    }
} catch (e) {}

module.exports = {

    // fake clientStorage API in case if clientStorage is disabled
    //clientStorage :  window.sessionStorage ? window.sessionStorage : {
    //    setItem : function () {},
    //    getItem : function () {},
    //    clear : function () {}
    //},
    clientStorage: ClientStorage,

    getCsrfToken: utils.getCsrfToken,
    ENTER_KEY: 13,
    OK: "OK",
    baseUrl: _baseUrl,

    loadConfig (pageConfig) {

        this.config = {};

        // Retrieve data and config printed by the server.
        if (window.Swiftref) {
            Object.assign(this, window.Swiftref);

            // merge laravel/config with js config file (if any)
            if (window.Swiftref.config) {
                Object.assign(this.config, window.Swiftref.config);
                delete window.Swiftref.config;
            }
        }

        // load client base config
        Object.assign(this.config, baseConfig);

        if (pageConfig && _.isObject(pageConfig)) {
            Object.assign(this.config, pageConfig);
        }


    },
    clone (obj) {
        try {
            return JSON.parse(JSON.stringify(obj));
        } catch (e) {
            return obj;
        }
    }
};



